import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { FilterAlt, History, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import { apiService } from '../services/api';
import { FiltroPorPeriodo, FiltroPorPeriodoValue } from './FiltroPorPeriodo';
import { ActiveFiltersBar } from './ActiveFiltersBar';
import { TooltipWhenDisabled } from './TooltipWhenDisabled';
import { AssuntoMonitoringConfirmDialog } from './AssuntoMonitoringConfirmDialog';
import { KeywordMonitoringConfirmDialog } from './KeywordMonitoringConfirmDialog';
import { LegislacaoSyncStatus } from './LegislacaoSyncStatus';
import { MonitorarAgenciasButton } from './monitoring';
import { AssuntoOptionLabel } from './assunto/AssuntoOptionLabel';
import { formatOfficialDateBR } from '../utils/officialDate';
import { normalizeAssuntoKey } from '../utils/assuntoMonitoring';
import {
  KEYWORD_SEARCH_SCOPE_INFO,
  KEYWORD_SEARCH_SCOPE_TITLE,
  KEYWORD_VALIDATION_HINT,
  normalizeKeywordKey,
  sanitizeSearchKeyword,
} from '../utils/keywordSearch';
import { FieldInfoPopper } from './layout/FieldInfoPopper';
import {
  type AssuntoStructuredOption,
  formatAssuntoFilterValue,
  formatAssuntoLabel,
} from '../utils/assuntoDisplay';
import type { RootState } from '../store';

export interface MudancasMonitoramentoFiltrosProps {
  periodFilter: FiltroPorPeriodoValue;
  onPeriodChange: (value: FiltroPorPeriodoValue) => void;
  assuntoFilter: string | null;
  onAssuntoFilterChange: (value: string | null) => void;
  keywordFilter: string | null;
  onKeywordFilterChange: (value: string | null) => void;
  onOpenAgencyMonitoring: () => void;
  onOpenMonitoringHistory: () => void;
  publicationDateMin?: string | null;
  actionsMonitoredNormAssuntoFilter?: boolean;
  onActionsMonitoredNormAssuntoFilterClear?: () => void;
  actionsMonitoredAgencyFilter?: boolean;
  onActionsMonitoredAgencyFilterClear?: () => void;
  greeningQaFilter?: boolean;
  onGreeningQaFilterClear?: () => void;
  extraActiveFilters?: Array<{
    key: string;
    label: string;
    onRemove: () => void;
  }>;
  onClearExtraFilters?: () => void;
}

export function MudancasMonitoramentoFiltros({
  periodFilter,
  onPeriodChange,
  assuntoFilter,
  onAssuntoFilterChange,
  keywordFilter,
  onKeywordFilterChange,
  onOpenAgencyMonitoring,
  onOpenMonitoringHistory,
  publicationDateMin = null,
  actionsMonitoredNormAssuntoFilter = false,
  onActionsMonitoredNormAssuntoFilterClear,
  actionsMonitoredAgencyFilter = false,
  onActionsMonitoredAgencyFilterClear,
  greeningQaFilter = false,
  onGreeningQaFilterClear,
  extraActiveFilters = [],
  onClearExtraFilters,
}: MudancasMonitoramentoFiltrosProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);
  const [assuntoInput, setAssuntoInput] = useState('');
  const [pendingAssunto, setPendingAssunto] = useState<AssuntoStructuredOption | null>(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [assuntoConfirmOpen, setAssuntoConfirmOpen] = useState(false);
  const [keywordConfirmOpen, setKeywordConfirmOpen] = useState(false);
  const assuntosErrorNotified = useRef(false);

  const { data: assuntoOptions = [], isFetching: assuntosFetching, isError: assuntosError } = useQuery(
    ['changes-assuntos-structured', assuntoInput],
    () => apiService.getChangesAssuntoStructuredOptions(assuntoInput.trim() || undefined),
    { staleTime: 60_000, keepPreviousData: true, retry: 1 },
  );

  useEffect(() => {
    if (assuntosError && !assuntosErrorNotified.current) {
      assuntosErrorNotified.current = true;
      toast.error('Não foi possível carregar assuntos. Verifique a conexão ou tente novamente.');
    }
    if (!assuntosError) {
      assuntosErrorNotified.current = false;
    }
  }, [assuntosError]);

  const { data: monitoredAssuntos } = useQuery(
    ['assuntos-monitoring'],
    () => apiService.getAssuntosMonitoring(),
    { staleTime: 30_000 },
  );

  const { data: monitoredKeywords } = useQuery(
    ['keywords-monitoring'],
    () => apiService.getKeywordsMonitoring(),
    { staleTime: 30_000 },
  );

  const activeAssuntoKeys = useMemo(
    () => new Set((monitoredAssuntos?.assuntos ?? []).map((a) => a.assunto_key)),
    [monitoredAssuntos?.assuntos],
  );

  const activeKeywordKeys = useMemo(
    () => new Set((monitoredKeywords?.keywords ?? []).map((k) => k.keyword_key)),
    [monitoredKeywords?.keywords],
  );

  const isCurrentAssuntoMonitored = Boolean(
    assuntoFilter && activeAssuntoKeys.has(normalizeAssuntoKey(assuntoFilter)),
  );

  const isCurrentKeywordMonitored = Boolean(
    keywordFilter && activeKeywordKeys.has(normalizeKeywordKey(keywordFilter)),
  );

  const bothFiltersActive = Boolean(assuntoFilter && keywordFilter);

  const canMonitorAssunto =
    Boolean(assuntoFilter) && !keywordFilter && !isCurrentAssuntoMonitored;
  const canMonitorKeyword =
    Boolean(keywordFilter) && !assuntoFilter && !isCurrentKeywordMonitored;
  const canMonitor = canMonitorAssunto || canMonitorKeyword;

  const assuntoFilterDisplay = useMemo(() => {
    if (!assuntoFilter) return '';
    const match = assuntoOptions.find((o) => o.filter_value === assuntoFilter);
    return match ? formatAssuntoLabel(match) : formatAssuntoFilterValue(assuntoFilter);
  }, [assuntoFilter, assuntoOptions]);

  const monitorAssuntoMutation = useMutation(
    (assunto: string) => apiService.activateAssuntoMonitoring(assunto),
    {
      onSuccess: async (data) => {
        await Promise.all([
          queryClient.invalidateQueries(['assuntos-monitoring']),
          queryClient.invalidateQueries(['assuntos-monitoring-history']),
          queryClient.invalidateQueries(['dashboard-changes']),
        ]);
        toast.success(data.message);
        setAssuntoConfirmOpen(false);
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'Erro ao ativar monitoramento do assunto';
        toast.error(message);
      },
    },
  );

  const monitorKeywordMutation = useMutation(
    (keyword: string) => apiService.activateKeywordMonitoring(keyword),
    {
      onSuccess: async (data) => {
        await Promise.all([
          queryClient.invalidateQueries(['keywords-monitoring']),
          queryClient.invalidateQueries(['keywords-monitoring-history']),
          queryClient.invalidateQueries(['dashboard-changes']),
        ]);
        toast.success(data.message);
        setKeywordConfirmOpen(false);
      },
      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : 'Erro ao ativar monitoramento da palavra-chave';
        toast.error(message);
      },
    },
  );

  const handleOpenConfirmMonitor = () => {
    if (bothFiltersActive || !canMonitor) return;
    if (canMonitorAssunto) setAssuntoConfirmOpen(true);
    else if (canMonitorKeyword) setKeywordConfirmOpen(true);
  };

  const monitorButtonLabel = useMemo(() => {
    if (bothFiltersActive) return 'Monitorar';
    if (canMonitorAssunto) return isCurrentAssuntoMonitored ? 'Monitorando' : 'Monitorar';
    if (canMonitorKeyword) return isCurrentKeywordMonitored ? 'Monitorando' : 'Monitorar';
    if (assuntoFilter && isCurrentAssuntoMonitored) return 'Monitorando';
    if (keywordFilter && isCurrentKeywordMonitored) return 'Monitorando';
    return 'Monitorar';
  }, [
    bothFiltersActive,
    canMonitorAssunto,
    canMonitorKeyword,
    isCurrentAssuntoMonitored,
    isCurrentKeywordMonitored,
    assuntoFilter,
    keywordFilter,
  ]);

  const monitorButtonTooltip = useMemo(() => {
    if (canMonitor) return '';
    if (bothFiltersActive) {
      return 'Para monitorar, use apenas assunto ou palavra-chave (não os dois juntos).';
    }
    if (assuntoFilter && isCurrentAssuntoMonitored) {
      return 'Este assunto já está sendo monitorado.';
    }
    if (keywordFilter && isCurrentKeywordMonitored) {
      return 'Esta palavra-chave já está sendo monitorada.';
    }
    if (!assuntoFilter && !keywordFilter) {
      return 'Clique em Selecionar após escolher um assunto ou digitar uma palavra-chave.';
    }
    return '';
  }, [
    canMonitor,
    bothFiltersActive,
    assuntoFilter,
    keywordFilter,
    isCurrentAssuntoMonitored,
    isCurrentKeywordMonitored,
  ]);

  const selectAssuntoTooltip = pendingAssunto
    ? ''
    : 'Digite e escolha um assunto na lista suspensa; depois clique em Selecionar.';

  const selectKeywordTooltip = keywordInput.trim()
    ? ''
    : 'Digite uma palavra-chave (ex.: pneu, efluente) e clique em Selecionar.';

  const activeFilters = useMemo(() => {
    const items: Array<{
      key: string;
      label: string;
      onRemove: () => void;
      icon?: 'calendar';
    }> = [];
    if (periodFilter.start) {
      items.push({
        key: 'pub-start',
        label: `Publicação de: ${formatOfficialDateBR(periodFilter.start)}`,
        onRemove: () => onPeriodChange({ ...periodFilter, start: null }),
        icon: 'calendar',
      });
    }
    if (periodFilter.end) {
      items.push({
        key: 'pub-end',
        label: `Publicação até: ${formatOfficialDateBR(periodFilter.end)}`,
        onRemove: () => onPeriodChange({ ...periodFilter, end: null }),
        icon: 'calendar',
      });
    }
    if (actionsMonitoredNormAssuntoFilter) {
      items.push({
        key: 'actions-monitored-norm-assunto',
        label: 'Assuntos e palavras-chave monitorados',
        onRemove: () => onActionsMonitoredNormAssuntoFilterClear?.(),
      });
    }
    if (actionsMonitoredAgencyFilter) {
      items.push({
        key: 'actions-monitored-agency',
        label: 'Agências monitoradas',
        onRemove: () => onActionsMonitoredAgencyFilterClear?.(),
      });
    }
    if (greeningQaFilter) {
      items.push({
        key: 'greening-qa',
        label: 'Conversas com perguntas e respostas (GreeningAI)',
        onRemove: () => onGreeningQaFilterClear?.(),
      });
    }
    if (assuntoFilter) {
      items.push({
        key: 'assunto',
        label: `Assunto: ${assuntoFilterDisplay}`,
        onRemove: () => {
          onAssuntoFilterChange(null);
          setPendingAssunto(null);
        },
      });
    }
    if (keywordFilter) {
      items.push({
        key: 'keyword',
        label: `Palavra-chave: ${keywordFilter}`,
        onRemove: () => {
          onKeywordFilterChange(null);
          setKeywordInput('');
        },
      });
    }
    for (const extra of extraActiveFilters) {
      items.push(extra);
    }
    return items;
  }, [
    assuntoFilter,
    assuntoFilterDisplay,
    keywordFilter,
    actionsMonitoredNormAssuntoFilter,
    actionsMonitoredAgencyFilter,
    greeningQaFilter,
    extraActiveFilters,
    onActionsMonitoredNormAssuntoFilterClear,
    onActionsMonitoredAgencyFilterClear,
    onGreeningQaFilterClear,
    onAssuntoFilterChange,
    onKeywordFilterChange,
    onPeriodChange,
    periodFilter,
  ]);

  const handleSelectAssunto = () => {
    if (!pendingAssunto) return;
    onAssuntoFilterChange(pendingAssunto.filter_value);
  };

  const handleSelectKeyword = () => {
    const cleaned = sanitizeSearchKeyword(keywordInput);
    if (!cleaned) {
      toast.error(KEYWORD_VALIDATION_HINT);
      return;
    }
    onKeywordFilterChange(cleaned);
    setKeywordInput(cleaned);
  };

  const monitorButton = (
    <Button
      variant="contained"
      color="success"
      size="medium"
      disabled={!canMonitor}
      startIcon={<Visibility />}
      onClick={handleOpenConfirmMonitor}
      sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
    >
      {monitorButtonLabel}
    </Button>
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <FiltroPorPeriodo
            value={periodFilter}
            onChange={onPeriodChange}
            label="FILTRAR POR:"
            totalSinceDate={publicationDateMin}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
          <Box sx={{ minWidth: 280, flex: 1, maxWidth: 560 }}>
            <Typography
              variant="caption"
              component="label"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5, fontWeight: 600, lineHeight: 1.2 }}
            >
              Buscar assunto para monitorar
            </Typography>
            <Autocomplete
              size="small"
              options={assuntoOptions}
              loading={assuntosFetching}
              inputValue={assuntoInput}
              onInputChange={(_, value) => setAssuntoInput(value)}
              value={pendingAssunto}
              onChange={(_, value) => setPendingAssunto(value)}
              getOptionLabel={(opt) => formatAssuntoLabel(opt)}
              isOptionEqualToValue={(a, b) => a.filter_value === b.filter_value}
              noOptionsText={
                assuntosFetching
                  ? 'Carregando…'
                  : assuntosError
                    ? 'Não foi possível carregar assuntos. Tente novamente.'
                    : 'Nenhum assunto encontrado'
              }
              renderOption={(props, option) => (
                <li {...props} key={option.filter_value}>
                  <AssuntoOptionLabel option={option} inline />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Pesca, Recuperação de área, Turismo…"
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Buscar assunto para monitorar',
                  }}
                />
              )}
            />
          </Box>
          <TooltipWhenDisabled title={selectAssuntoTooltip} disabled={!pendingAssunto}>
            <Button
              variant="contained"
              size="medium"
              disabled={!pendingAssunto}
              startIcon={<FilterAlt />}
              onClick={handleSelectAssunto}
              sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
            >
              Selecionar
            </Button>
          </TooltipWhenDisabled>
          <TooltipWhenDisabled title={monitorButtonTooltip} disabled={!canMonitor}>
            {monitorButton}
          </TooltipWhenDisabled>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5, mt: 1.5 }}>
          <Box sx={{ minWidth: 280, flex: 1, maxWidth: 560 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography
                variant="caption"
                component="label"
                color="text.secondary"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
              >
                Busca por palavra-chave
              </Typography>
              <FieldInfoPopper
                title={KEYWORD_SEARCH_SCOPE_TITLE}
                text={KEYWORD_SEARCH_SCOPE_INFO}
                placement="right"
                ariaLabel="Informações sobre busca por palavra-chave"
              />
            </Box>
            <TextField
              size="small"
              fullWidth
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder="Ex.: pneu, efluente, resíduo…"
              inputProps={{ 'aria-label': 'Busca por palavra-chave' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSelectKeyword();
                }
              }}
            />
          </Box>
          <TooltipWhenDisabled title={selectKeywordTooltip} disabled={!keywordInput.trim()}>
            <Button
              variant="contained"
              size="medium"
              disabled={!keywordInput.trim()}
              startIcon={<FilterAlt />}
              onClick={handleSelectKeyword}
              sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
            >
              Selecionar
            </Button>
          </TooltipWhenDisabled>
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5, ml: 'auto' }}
          >
            <MonitorarAgenciasButton onClick={onOpenAgencyMonitoring} />
            <Button
              variant="outlined"
              size="medium"
              startIcon={<History />}
              onClick={onOpenMonitoringHistory}
              sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
            >
              Histórico
            </Button>
            <LegislacaoSyncStatus compact />
          </Box>
        </Box>

        {(assuntoFilter || keywordFilter) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {assuntoFilter && (
              <>
                Tabela filtrada pelo assunto «{assuntoFilterDisplay}».
                {isCurrentAssuntoMonitored && ' Este assunto está sendo monitorado.'}
              </>
            )}
            {assuntoFilter && keywordFilter && ' '}
            {keywordFilter && (
              <>
                Palavra-chave «{keywordFilter}» aplicada (ementa e título).
                {isCurrentKeywordMonitored && ' Esta palavra-chave está sendo monitorada.'}
              </>
            )}
            {bothFiltersActive &&
              ' Os dois filtros estão ativos — use apenas um para habilitar Monitorar.'}
          </Typography>
        )}
      </Paper>

      {activeFilters.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <ActiveFiltersBar
            filters={activeFilters}
              onClearAll={() => {
              // "Limpar Todos" some com TODOS os chips, inclusive os de data.
              // Resetar para o padrão (este mês) mantinha os chips de período
              // na tela — parecia que o botão não fazia nada. 'total' remove a
              // restrição de data e esvazia a barra de filtros ativos.
              onPeriodChange({ start: null, end: null, preset: 'total' });
              onActionsMonitoredNormAssuntoFilterClear?.();
              onActionsMonitoredAgencyFilterClear?.();
              onGreeningQaFilterClear?.();
              onAssuntoFilterChange(null);
              onKeywordFilterChange(null);
              setPendingAssunto(null);
              setAssuntoInput('');
              setKeywordInput('');
              onClearExtraFilters?.();
            }}
          />
        </Box>
      )}

      <AssuntoMonitoringConfirmDialog
        open={assuntoConfirmOpen}
        assuntoLabel={assuntoFilterDisplay || assuntoFilter}
        userName={user?.name || user?.email}
        onClose={() => setAssuntoConfirmOpen(false)}
        onConfirm={() => {
          if (assuntoFilter) monitorAssuntoMutation.mutate(assuntoFilter);
        }}
        loading={monitorAssuntoMutation.isLoading}
      />

      <KeywordMonitoringConfirmDialog
        open={keywordConfirmOpen}
        keywordLabel={keywordFilter}
        userName={user?.name || user?.email}
        onClose={() => setKeywordConfirmOpen(false)}
        onConfirm={() => {
          if (keywordFilter) monitorKeywordMutation.mutate(keywordFilter);
        }}
        loading={monitorKeywordMutation.isLoading}
      />
    </Box>
  );
}
